import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

const ClientForm = ({ client, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: "",
    clientType: "External",
    status: "Active",
    accountOwner: "",
    accountOwnerId: "",
    primaryContactName: "",
    primaryContactEmail: "",
    primaryContactPhone: "",
    industry: "",
    sector: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || "",
        clientType: client.clientType || "External",
        status: client.status || "Active",
        accountOwner: client.accountOwner || "",
        accountOwnerId: client.accountOwnerId || "",
        primaryContactName: client.primaryContactName || "",
        primaryContactEmail: client.primaryContactEmail || "",
        primaryContactPhone: client.primaryContactPhone || "",
        industry: client.industry || "",
        sector: client.sector || "",
        notes: client.notes || "",
      });
    }
  }, [client]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = "Name is required";
    if (!formData.clientType) next.clientType = "Client type is required";
    if (
      formData.primaryContactEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.primaryContactEmail)
    ) {
      next.primaryContactEmail = "Enter a valid email";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        name: formData.name.trim(),
        primaryContactEmail: formData.primaryContactEmail || null,
        primaryContactName: formData.primaryContactName || null,
        primaryContactPhone: formData.primaryContactPhone || null,
        accountOwner: formData.accountOwner || null,
        accountOwnerId: formData.accountOwnerId || null,
        industry: formData.industry || null,
        sector: formData.sector || null,
        notes: formData.notes || null,
      });
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (Array.isArray(apiErrors)) {
        const mapped = {};
        apiErrors.forEach((item) => {
          if (item.path) mapped[item.path] = item.msg;
        });
        setErrors(mapped);
      } else {
        setErrors({
          name: err.response?.data?.error || "Failed to save client",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} mt={1}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Client name"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
            disabled={client?.name === "Nikao - Internal"}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth required>
            <InputLabel>Client type</InputLabel>
            <Select
              label="Client type"
              value={formData.clientType}
              onChange={(e) => handleChange("clientType", e.target.value)}
              disabled={client?.name === "Nikao - Internal"}
            >
              <MenuItem value="External">External</MenuItem>
              <MenuItem value="Internal">Internal</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth required>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={formData.status}
              onChange={(e) => handleChange("status", e.target.value)}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Account owner"
            value={formData.accountOwner}
            onChange={(e) => handleChange("accountOwner", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Industry"
            value={formData.industry}
            onChange={(e) => handleChange("industry", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Sector"
            value={formData.sector}
            onChange={(e) => handleChange("sector", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Primary contact name"
            value={formData.primaryContactName}
            onChange={(e) => handleChange("primaryContactName", e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Primary contact email"
            value={formData.primaryContactEmail}
            onChange={(e) =>
              handleChange("primaryContactEmail", e.target.value)
            }
            error={Boolean(errors.primaryContactEmail)}
            helperText={errors.primaryContactEmail}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Primary contact phone"
            value={formData.primaryContactPhone}
            onChange={(e) =>
              handleChange("primaryContactPhone", e.target.value)
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Notes"
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {client ? "Update" : "Create"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientForm;
